/**
 * Which plants are new to this device, and what to say about them (SPEC-016).
 *
 * A new plant reaches this app the way every other change does: someone drops a
 * content pack in, the catalogue is rebuilt, and the site is deployed. There is
 * no event and no server to send one — the arrival is simply a `catalogue.json`
 * with one more record in it than the copy this device last read. So "a new
 * plant was added" is a DIFF, computed here, against a list of ids the device
 * remembers.
 *
 * The first run is the case that matters. A device that has never recorded a
 * catalogue has not missed twenty-five arrivals; it has missed none, and firing
 * twenty-five notifications at someone who just opened the app for the second
 * time is the single worst thing this feature could do. So the first read SEEDS
 * and announces nothing, and `hasSeenCatalogue` is the flag that says which run
 * this is — deliberately separate from "the seen list is empty", which is also
 * true of a device whose storage was cleared.
 *
 * Pure. No fetching, no storage, no Notification API.
 */

/**
 * Above this, the notification counts rather than names.
 *
 * One plant is news about that plant and links to it. Four are news about the
 * catalogue, and four notifications for one deploy is a spam report.
 */
export const MAX_NAMED_ARRIVALS = 1;

function toIdList(value) {
    return Array.isArray(value)
        ? value.filter((id) => typeof id === 'string' && id.trim() !== '')
        : [];
}

/**
 * @param {object} input
 * @param {string[]} input.cataloguePlantIds Ids in the catalogue as just read.
 * @param {string[]} input.seenPlantIds Ids this device has already been told about.
 * @param {boolean} input.hasSeenCatalogue Whether this device ever recorded a catalogue.
 * @returns {{arrivals: string[], nextSeenPlantIds: string[], isSeeding: boolean}}
 */
export function selectPlantArrivals({
    cataloguePlantIds = [],
    seenPlantIds = [],
    hasSeenCatalogue = false,
} = {}) {
    const catalogue = toIdList(cataloguePlantIds);

    // Nothing was read — an offline poll, a failed fetch, a hosting hiccup that
    // answered with the app shell. Recording that as "the catalogue is now
    // empty" would announce every plant as new on the next successful read.
    if (catalogue.length === 0) {
        return { arrivals: [], nextSeenPlantIds: toIdList(seenPlantIds), isSeeding: false };
    }

    if (!hasSeenCatalogue) {
        return { arrivals: [], nextSeenPlantIds: catalogue, isSeeding: true };
    }

    const seen = new Set(toIdList(seenPlantIds));

    return {
        arrivals: catalogue.filter((plantId) => !seen.has(plantId)),
        // The catalogue itself, not the union with what was seen before: the
        // store stays the size of the catalogue rather than growing forever,
        // and a plant that is withdrawn and later restored is news again, which
        // is the truthful answer for a device that has not seen it in between.
        nextSeenPlantIds: catalogue,
        isSeeding: false,
    };
}

/**
 * The notification for a set of arrivals, as message KEYS and values.
 *
 * Keys rather than sentences because this is domain code and the language is
 * not its business — the caller resolves them, whether that caller is the app
 * holding a `t()` or the service worker holding a copy bundle.
 *
 * `tag` is constant on purpose: a second arrival replaces the first in the
 * shade rather than stacking beneath it. Someone who was away for three deploys
 * should find one notification about the catalogue, not three.
 */
export function buildArrivalNotification({ arrivals = [], plantNames = {}, localeCode = '' } = {}) {
    const newPlantIds = toIdList(arrivals);

    if (newPlantIds.length === 0) {
        return null;
    }

    const localePrefix = typeof localeCode === 'string' && localeCode ? `/${localeCode}` : '';

    if (newPlantIds.length <= MAX_NAMED_ARRIVALS) {
        const [plantId] = newPlantIds;
        const plantName = typeof plantNames[plantId] === 'string' && plantNames[plantId].trim()
            ? plantNames[plantId].trim()
            : plantId;

        return {
            titleKey: 'pwa.arrival.one.title',
            bodyKey: 'pwa.arrival.one.body',
            values: { plant: plantName },
            url: `${localePrefix}/plants/${plantId}`,
            tag: 'plant-arrival',
            plantIds: newPlantIds,
        };
    }

    return {
        titleKey: 'pwa.arrival.many.title',
        bodyKey: 'pwa.arrival.many.body',
        values: { count: newPlantIds.length },
        url: `${localePrefix}/plants`,
        tag: 'plant-arrival',
        plantIds: newPlantIds,
    };
}
